import { faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    getClientPaymentMethodsThunk,
    getPspProfileThunk,
    getSurchargeThunk,
    payByCardOnFileThunk,
    PaymentsStatus
} from '@spike/payments-action';
import { ClientPaymentMethod, PspProfile, Surcharge } from '@spike/payments-model';
import useNonInitialEffect from '@versiondos/hooks';
import clsx from 'clsx';
import AdyenPayment, { CompletedInfo } from 'components/Payments/Adyen/AdyenPayment';
import { CalculatingSpinner } from 'components/UI';
import SelectableCreditCard from 'components/UI/V2/SelectableCreditCard/SelectableCreditCard';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import { useApiClientWrapper, usePayByLinkApiClientWrapper } from 'hooks';
import { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import CheckoutHeader from './CheckoutHeader';
import styles from './CreditCardPayment2.module.scss';
import Alert from './SplitPayments/Alert';
import { PaymentLink } from 'actions/payByLink/PayByLinkDto';

interface CreditCardPaymentProps {
    invoiceUuid: string;
    invoiceId: number;
    clientId: number;
    amount?: number;
    depositAmount?: number;
    paymentLink?: PaymentLink;
    className?: string;
    onSuccess: (paidAmount: number) => void;
    onBack?: () => void;
    onChangeSurcharge?: (surcharge: number) => void;
}

const formatMoney = (value: number): string => `$${Number(value).toFixed(2)}`;

const formatSurchargeRate = (rate: number): string | undefined => {
    if (!Number.isFinite(rate) || rate <= 0) {
        return undefined;
    }

    const rounded = Math.round(rate * 100) / 100;

    return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(2).replace(/\.?0+$/, '');
};

interface SummaryRowProps {
    label: string;
    value: string;
    loading?: boolean;
    isTotal?: boolean;
    withDivider?: boolean;
}

const SummaryRow: FunctionComponent<SummaryRowProps> = ({ label, value, loading, isTotal, withDivider }) => (
    <div className={clsx(styles.summaryRow, isTotal && styles.summaryTotal, withDivider && styles.summaryTotalDivider)}>
        <span>{label}</span>
        <span className={styles.summaryValue}>{loading ? <CalculatingSpinner /> : value}</span>
    </div>
);

export const CreditCardPayment: FunctionComponent<CreditCardPaymentProps> = props => {
    const dispatch = useDispatch();
    const standardApiClientWrapper = useApiClientWrapper();
    const payByLinkApiClientWrapper = usePayByLinkApiClientWrapper(props.paymentLink);
    const apiClientWrapper = props.paymentLink === undefined ? standardApiClientWrapper : payByLinkApiClientWrapper;

    const paymentsStatus = useSelector<RootState, PaymentsStatus>(state => state.payments.status);
    const clientCreditCards = useSelector<RootState, ClientPaymentMethod[]>(
        state => state.payments.clientPaymentMethods
    );
    const cardOnFileErrorMessage = useSelector<RootState, string | undefined>(
        state => state.payments.cardOnFile.errorMessage
    );
    const surcharge = useSelector<RootState, Surcharge | undefined>(state => state.payments.surcharge);
    const pspProfile = useSelector<RootState, PspProfile | undefined>(state => state.payments.pspProfile);

    const sortedClientCreditCards = useMemo(
        () => clientCreditCards.sort((a, b) => (a.primary === b.primary ? 0 : a.primary ? -1 : 1)),
        [clientCreditCards]
    );
    const clientHasCreditCards = useMemo(() => clientCreditCards.length > 0, [clientCreditCards]);

    const [loading, setLoading] = useState({ creditCards: true, payment: false, surcharge: false, pspProfile: true });
    const [show, setShow] = useState({ list: true, add: false, error: false });
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [selectedCreditCard, setSelectedCreditCard] = useState<ClientPaymentMethod | null>(null);
    const isPayingRef = useRef(false);
    const displayedAmountRef = useRef(0);
    const pspProfilePendingRef = useRef(true);

    const isSurchargeMethod = pspProfile?.processingMethod === 'surcharge';
    const isInitialLoading = loading.pspProfile || loading.creditCards;

    const showSurcharge = useMemo(() => {
        return (
            isSurchargeMethod &&
            selectedCreditCard?.enabledSurcharge &&
            surcharge !== undefined &&
            surcharge.customerId === props.clientId &&
            surcharge.paymentMethodId === selectedCreditCard.id
        );
    }, [isSurchargeMethod, selectedCreditCard, surcharge, props.clientId]);

    const baseAmount = props.amount ?? props.depositAmount ?? 0;
    const surchargeAmount = showSurcharge ? surcharge?.surcharge.amount ?? 0 : 0;
    const taxOnSurchargeAmount = showSurcharge ? surcharge?.surcharge.tax.amount ?? 0 : 0;
    const surchargePercent = showSurcharge ? formatSurchargeRate(surcharge?.surcharge.rate ?? 0) : undefined;

    const displayedAmount = useMemo(() => {
        return showSurcharge ? surcharge?.totalWithSurcharge ?? baseAmount : baseAmount;
    }, [showSurcharge, surcharge, baseAmount]);
    displayedAmountRef.current = displayedAmount;

    const fetchClientPaymentMethods = () => {
        dispatch(getClientPaymentMethodsThunk(apiClientWrapper, props.clientId));
    };

    useEffect(() => {
        pspProfilePendingRef.current = true;
        setLoading(prev => ({ ...prev, pspProfile: true, creditCards: true }));
        dispatch(getPspProfileThunk(apiClientWrapper));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useNonInitialEffect(() => {
        switch (paymentsStatus) {
            case PaymentsStatus.GetPspProfileSuccess:
                if (pspProfilePendingRef.current) {
                    pspProfilePendingRef.current = false;
                    setLoading(prev => ({ ...prev, pspProfile: false }));
                    if (pspProfile?.processingMethod !== 'surcharge') {
                        props.onChangeSurcharge?.(0);
                    }
                    fetchClientPaymentMethods();
                }
                break;
            case PaymentsStatus.GetClientPaymentMethodsSuccess:
                setLoading(prev => ({ ...prev, creditCards: false }));
                break;
            case PaymentsStatus.PayByCardOnFileSuccess:
                isPayingRef.current = false;
                props.onSuccess(displayedAmountRef.current);
                break;
            case PaymentsStatus.GetSurchargeSuccess:
                setLoading(prev => ({ ...prev, surcharge: false }));
                props.onChangeSurcharge?.(surcharge?.surcharge.total ?? 0);
                break;
            case PaymentsStatus.Error:
            case PaymentsStatus.GetClientPaymentMethodsError:
            case PaymentsStatus.GetDropInAddPaymentMethodSessionError:
                if (pspProfilePendingRef.current) {
                    pspProfilePendingRef.current = false;
                    setLoading(prev => ({ ...prev, pspProfile: false }));
                    props.onChangeSurcharge?.(0);
                    fetchClientPaymentMethods();
                    break;
                }
                isPayingRef.current = false;
                setLoading(prev => ({ ...prev, creditCards: false, payment: false, surcharge: false }));
                showList();
                break;
            case PaymentsStatus.PayByCardOnFileError:
                isPayingRef.current = false;
                setErrorMessage(
                    cardOnFileErrorMessage ?? 'Something went wrong while processing your payment. Please try again.'
                );
                setLoading(prev => ({ ...prev, payment: false }));
                showError();
                break;
        }
    }, [paymentsStatus, pspProfile]);

    const showList = () => {
        setShow(prev => ({ list: true, add: false, error: false }));
    };

    const showAdd = () => {
        setShow(prev => ({ list: false, add: true, error: false }));
    };

    const showError = () => {
        setShow(prev => ({ list: false, add: false, error: true }));
    };

    const creditCardAddedHandler = (info: CompletedInfo) => {
        if (info.success) {
            setLoading(prev => ({ ...prev, creditCards: true }));
            setTimeout(() => {
                fetchClientPaymentMethods();
            }, 2500);
            showList();
        } else {
            setErrorMessage(info.message);
            showError();
        }
    };

    const backHandler = () => {
        if (show.list) {
            props.onBack?.();
        } else {
            showList();
        }
    };

    const getHeaderTitle = () => {
        if (show.list) {
            return 'Select Credit Card';
        } else if (show.add) {
            return 'Add Credit Card';
        } else if (show.error) {
            return 'Error';
        } else {
            return 'Credit Card';
        }
    };

    const buttonClickHandler = () => {
        if (show.list) {
            if (!selectedCreditCard || loading.payment || isPayingRef.current) {
                return;
            }

            isPayingRef.current = true;
            setLoading(prev => ({ ...prev, payment: true }));
            dispatch(
                payByCardOnFileThunk(
                    apiClientWrapper,
                    props.invoiceUuid,
                    selectedCreditCard.uuid,
                    props.amount,
                    props.paymentLink !== undefined
                )
            );
        } else if (show.error) {
            showList();
        }
    };

    const errorHandler = (errorMessage: string) => {
        showError();
        setErrorMessage(errorMessage);
    };

    const selectCardHandler = (paymentMethod: ClientPaymentMethod) => {
        setSelectedCreditCard(paymentMethod);
        if (isSurchargeMethod && paymentMethod.enabledSurcharge) {
            setLoading(prev => ({ ...prev, surcharge: true }));
            dispatch(
                getSurchargeThunk(
                    apiClientWrapper,
                    props.amount ?? props.depositAmount ?? 0,
                    paymentMethod.id,
                    props.clientId,
                    props.invoiceId
                )
            );
        } else {
            setLoading(prev => ({ ...prev, surcharge: false }));
            props.onChangeSurcharge?.(0);
        }
    };

    return (
        <div
            className={clsx(styles.container, props.className)}
            style={{ height: '100%' }}
        >
            <CheckoutHeader
                title={getHeaderTitle()}
                onBack={props.onBack === undefined && show.list ? undefined : backHandler}
                className={styles.header}
            />
            <div className={styles.body}>
                {isInitialLoading && (
                    <div className={styles.spinnerContainer}>
                        <Spinner />
                    </div>
                )}
                {show.list && !isInitialLoading && (
                    <div className={styles.creditCardListContainer}>
                        {sortedClientCreditCards.map(creditCard => (
                            <SelectableCreditCard
                                key={creditCard.id}
                                card={creditCard}
                                selected={selectedCreditCard?.id === creditCard.id}
                                onSelect={selectCardHandler}
                            />
                        ))}
                    </div>
                )}
                {show.list && !isInitialLoading && !clientHasCreditCards && (
                    <Alert message="This client has no credit cards on file." />
                )}
                {show.list && !isInitialLoading && (
                    <div className={styles.addContainer}>
                        <span
                            className={styles.add}
                            onClick={showAdd}
                        >
                            + Add New Card
                        </span>
                    </div>
                )}
                {show.add && (
                    <AdyenPayment
                        clientId={props.clientId}
                        onError={errorHandler}
                        actionType="addPaymentMethod"
                        paymentLink={props.paymentLink}
                        onComplete={creditCardAddedHandler}
                        hideRemovePaymentMethodButton
                        hideStoredPaymentMethods
                    />
                )}
                {show.error && (
                    <div className={styles.messageContainer}>
                        <FontAwesomeIcon
                            icon={faCircleXmark}
                            className={clsx(styles.icon, styles.errorIcon)}
                        />
                        <span className={styles.error}>{errorMessage ?? 'Error!'}</span>
                    </div>
                )}
            </div>
            {!show.add && !isInitialLoading && (
                <div className={styles.footer}>
                    {show.list && (
                        <div className={styles.summary}>
                            {isSurchargeMethod && (
                                <>
                                    <SummaryRow
                                        label="Amount to Pay"
                                        value={formatMoney(baseAmount)}
                                    />
                                    <SummaryRow
                                        label={
                                            surchargePercent
                                                ? `Credit Card Surcharge (${surchargePercent}%)`
                                                : 'Credit Card Surcharge'
                                        }
                                        value={formatMoney(surchargeAmount)}
                                        loading={loading.surcharge}
                                    />
                                    <SummaryRow
                                        label="Tax on Surcharge"
                                        value={formatMoney(taxOnSurchargeAmount)}
                                        loading={loading.surcharge}
                                    />
                                </>
                            )}
                            <SummaryRow
                                label="Total"
                                value={formatMoney(displayedAmount)}
                                loading={isSurchargeMethod && loading.surcharge}
                                isTotal
                                withDivider={isSurchargeMethod}
                            />
                        </div>
                    )}
                    <Button
                        label={show.error ? 'Back' : 'Pay'}
                        variant="black"
                        fullWidth={true}
                        className={styles.payButton}
                        disabled={
                            loading.payment ||
                            (isSurchargeMethod && loading.surcharge) ||
                            (!show.error && selectedCreditCard === null)
                        }
                        isLoading={loading.payment}
                        onClick={buttonClickHandler}
                    />
                </div>
            )}
        </div>
    );
};

export default CreditCardPayment;
